import { supervisedAssuranceComplete } from '@api/selfServiceServices';
import {
  Authn,
  AuthnMethod,
  ContextType,
  EventType,
  RetryableError as RetryableErrorAuthn,
} from '@imprivata-cloud/authn';
import { jsonToBase64 } from '@imprivata-cloud/dataprivacy';
import { AuthnCard } from '@selfservice/containers/EpcsSetup/AuthnCard/AuthnCard';
import type { PKCECodes } from '@selfservice/utils/pkce';
import { useSelfServiceData } from '@selfservice/utils/useSelfServiceData';
import './EpcsStep4SupervisorAuthenticate.less';
import WitnessIcon from '@assets/icons/witness.svg?react';
import Separator from '@components/separator/Separator';
import { ErrorCode } from '@errorHandler/constants';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { RetryableError } from '@imprivata-cloud/core';
import { useTranslation } from 'react-i18next';

const DEFAULT_DEVICE_FACTORS: AuthnMethod[] = [
  AuthnMethod.PASSWORD,
  AuthnMethod.IMPR_ID,
  AuthnMethod.IMPR_PIN,
  AuthnMethod.TEMP_CODE,
];

interface SupervisorAuthenticateProps {
  assuranceId: string;
  pkceCodes: PKCECodes;
  tenantId?: string;
  workflowId: string;
  cancelFlow: () => void;
  completeFlow: () => void;
}

const TRANSLATION = 'self-service.epcs-setup.supervisor-authenticate';

const EpcsStep4SupervisorAuthenticate = ({
  assuranceId,
  pkceCodes,
  tenantId,
  workflowId,
  cancelFlow,
  completeFlow,
}: SupervisorAuthenticateProps) => {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  const { data } = useSelfServiceData();

  if (!pkceCodes.codeChallenge) {
    return null;
  }

  return (
    <div className="supervisor-authenticate">
      <div className="supervisor-authenticate-steps">
        <h1>
          <WitnessIcon /> {t(`${TRANSLATION}.title`)}
        </h1>
        <Separator />
        <p>{t(`${TRANSLATION}.instructions`)}</p>
      </div>
      <div>
        <AuthnCard logoUrl={data?.orgInfo?.logoUrl}>
          <Authn
            deviceFactors={DEFAULT_DEVICE_FACTORS}
            contextResource={{
              resourceType: 'user-web-ui',
              contextType: ContextType.SUPERVISED_ASSURANCE,
            }}
            tenantId={tenantId || ''}
            isTimerDisabled={true}
            hideFactorSwitch={true}
            requestConfig={{
              clientName: 'user-web',
              oidcRequestData: {
                codeChallenge: pkceCodes.codeChallenge,
                codeChallengeMethod: 'S256',
              },
              inAppSessionHeader: jsonToBase64({ contextType: ContextType.SUPERVISED_ASSURANCE }),
            }}
            tracing={{ workflowId }}
            onEvent={async ({ event, data }) => {
              // will remove this later once testing is done
              console.info(`Authn event: ${event}, data: ${data}, data: `, JSON.stringify(data));
              switch (event) {
                case EventType.AUTHENTICATED: {
                  if (data?.oidcAuthorizationCode) {
                    try {
                      await supervisedAssuranceComplete(
                        {
                          assuranceId,
                          oidcAuthorizationCode: data.oidcAuthorizationCode,
                          oidcCodeVerifier: pkceCodes.codeVerifier,
                        },
                        workflowId,
                        tenantId,
                      );
                      completeFlow();
                    } catch (error) {
                      emitError(new AppError(error as ApiError));
                      cancelFlow();
                    }
                  } else {
                    console.error('AUTHENTICATED but no oidcAuthorizationCode. Cancelling flow...');
                    emitError(new AppError({ code: ErrorCode.UNKNOWN }));
                    cancelFlow();
                  }
                  return;
                }
                case EventType.ERROR: {
                  console.warn(
                    `Authn event: ${event}, data: ${data}, data: `,
                    JSON.stringify(data),
                  );
                  if (!data?.code) {
                    return;
                  }
                  if (data instanceof RetryableErrorAuthn || data instanceof RetryableError) {
                    emitError(new AppError({ code: data.code as unknown as ErrorCode }));
                    return;
                  }
                  console.error('Not retryable error. Cancelling flow...');
                  emitError(new AppError({ code: data.code as unknown as ErrorCode }));
                  cancelFlow();
                  return;
                }
                default:
                  return;
              }
            }}
          />
        </AuthnCard>
      </div>
    </div>
  );
};

export default EpcsStep4SupervisorAuthenticate;
